import {
	FC,
	HTMLAttributes,
	ReactNode,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { Hint } from '@redoc/ui-kit';
import { ReactComponent as CopyIcon } from 'src/Themes/assets/copy.svg';
import { ReactComponent as EditIcon } from 'src/Themes/assets/Editing.svg';
import { useOutsideClick } from 'src/Hooks';
import API from 'src/API';
import Button from 'src/Components/Button';
import clx from 'classnames';
import copy from 'copy-to-clipboard';
import EditableFieldToolbarButton from './EditableFieldToolbarBautton/EditableFieldToolbarBautton';
import Input from '../Input/Input';
import UrlQualifier from 'src/Utils/UrlQualifier';
import st from './EditableField.module.scss';

interface IEditableFieldProps extends HTMLAttributes<HTMLDivElement> {
	type?: 'text' | 'textarea' | 'number' | 'custom';
	initValue?: string;
	placeholder?: string;
	readOnly?: boolean;
	allowCopy?: boolean;
	autoClose?: boolean;
	saveOnEnter?: boolean;
	saveOnOutsideClick?: boolean;
	focusCaretAtEnd?: boolean;
	min?: boolean;
	editHintPlacement?: 'top' | 'bottom' | 'left' | 'right';
	onChangeValue: (newValue: string) => void;
	renderView?: (props: any) => ReactNode;
}

const EditableField: FC<IEditableFieldProps> = (props) => {
	const {
		type = 'text',
		initValue = '',
		autoClose = true,
		min = false,
		readOnly = false,
		allowCopy = false,
		editHintPlacement = 'top',
		saveOnEnter = false,
		saveOnOutsideClick = false,
		focusCaretAtEnd = false,
		autoFocus,
		placeholder,
		onChangeValue,
		renderView,
		className,
		children,
		...otherProps
	} = props;

	const ref = useOutsideClick<HTMLDivElement>(() => {
		if (saveOnOutsideClick) {
			onSaveChangeRef.current();
		}
		return setEditMode(false);
	});
	const [editMode, setEditMode] = useState<boolean>(false);
	const [value, setValue] = useState<string>(initValue);
	const onSaveChangeRef = useRef<() => void>(() => {});

	useEffect(() => {
		setValue(initValue);
	}, [initValue, editMode]);

	// Устанавливаем фокус и каретку в конце начального значения
	useLayoutEffect(() => {
		if (!editMode || !focusCaretAtEnd) {
			return;
		}
		const editableElement = ref.current?.querySelector<
			HTMLInputElement | HTMLTextAreaElement
		>('input, textarea');
		if (!editableElement) {
			return;
		}
		editableElement.focus();
		editableElement.setSelectionRange(
			editableElement.value.length,
			editableElement.value.length,
		);
	}, [editMode, focusCaretAtEnd]);

	// Следим за нажатиями Enter и Esc
	useLayoutEffect(() => {
		const onKeyUp = (e: KeyboardEvent) => {
			if (e.code === 'Enter' && type !== 'textarea') {
				onSaveChangeRef.current();
			} else if (e.code === 'Escape') {
				onCloseEditMode();
			}
		};
		if (!editMode) {
			window.removeEventListener('keyup', onKeyUp);
			return;
		}
		window.addEventListener('keyup', onKeyUp);
		return () => {
			window.removeEventListener('keyup', onKeyUp);
		};
		// eslint-disable-next-line
	}, [editMode]);

	// Копируем значение в буфер обмена
	const onCopyHandler = () => {
		const isSuccess = copy(value, {
			debug: window.isDev,
		});
		const msg = isSuccess
			? 'Поле скопировано в буфер обмена'
			: 'Ошибка при копировании значения поля в буфер обмена';
		API.showToast(isSuccess, msg);
	};

	// Сохраняем значение и выходим из режима редактирования...
	const onSaveChange = () => {
		if (value !== initValue) {
			onChangeValue(value);
			if (autoClose) {
				onCloseEditMode();
			}
		}
	};
	onSaveChangeRef.current = onSaveChange;

	// Закрываем режим редактирования и сбрасываем значение до начального...
	const onCloseEditMode = () => {
		return setEditMode(false);
	};

	// Рендерим режим редактирования
	const renderEditMode = () => {
		return (
			<div ref={ref} className={clx(st.editWrapper, st[type], className)}>
				<Input
					type={type}
					autoFocus={autoFocus}
					onKeyDown={(event) => {
						if (
							saveOnEnter &&
							event.key === 'Enter' &&
							!event.nativeEvent.isComposing
						) {
							event.preventDefault();
							onSaveChange();
						}
					}}
					value={value}
					onChangeValue={(v) => {
						return setValue(v);
					}}
				/>
				<div className={st.editToolbar}>
					<EditableFieldToolbarButton
						type="apply"
						onClick={onSaveChange}
						disable={readOnly || !value || value === initValue}
					/>
					<EditableFieldToolbarButton
						type="cancel"
						onClick={onCloseEditMode}
					/>
				</div>
			</div>
		);
	};

	// Рендерим режим просмотра
	const renderViewMode = () => {
		return (
			<div className={clx(st.viewWrapper, className)} {...otherProps}>
				{renderView ? (
					renderView(props)
				) : (
					<div
						dangerouslySetInnerHTML={{
							__html: UrlQualifier.getReformated(initValue),
						}}
					/>
				)}
				<div className={clx(st.viewToolbar, { [st.min]: min })}>
					{allowCopy && value && (
						<Hint placement="top" messageText="Копировать">
							<Button
								className={st.copyButton}
								size="min"
								outline
								onClick={() => {
									return onCopyHandler();
								}}
							>
								<CopyIcon />
							</Button>
						</Hint>
					)}
					<Hint placement={editHintPlacement} messageText="Редактировать">
						<Button
							size="min"
							outline
							disable={readOnly}
							onClick={() => {
								return setEditMode(true);
							}}
						>
							<EditIcon />
						</Button>
					</Hint>
				</div>
			</div>
		);
	};

	return editMode ? renderEditMode() : renderViewMode();
};

export default EditableField;
 